import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Check, Wallet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { apiRequest } from "@/lib/queryClient";

// Load Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const CheckoutForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin,
      },
    });

    if (error) {
      toast({
        title: "Payment Failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Payment Successful",
        description: "Welcome to LPFTS WORLD WIDE!",
      });
      onSuccess();
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <Button 
        type="submit" 
        disabled={!stripe || isProcessing}
        className="w-full bg-neon-green text-dark-base font-semibold hover:bg-neon-green/90"
      >
        {isProcessing ? "Processing..." : "Complete Setup & Enter Game"}
      </Button>
    </form>
  );
};

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [clientSecret, setClientSecret] = useState("");
  const [walletConnected, setWalletConnected] = useState(false);
  const { toast } = useToast();

  // Create subscription on component mount
  useEffect(() => {
    if (step === 2) {
      apiRequest("POST", "/api/get-or-create-subscription")
        .then((response) => response.json())
        .then((data) => {
          setClientSecret(data.clientSecret);
        })
        .catch((error) => {
          console.error("Error creating subscription:", error);
          toast({
            title: "Setup Error",
            description: "Please contact support for payment setup assistance",
            variant: "destructive",
          });
        });
    }
  }, [step, toast]);

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ 
          method: 'eth_requestAccounts' 
        });
        setWalletConnected(true);
        toast({
          title: "Wallet Connected",
          description: `Connected to ${accounts[0].slice(0, 6)}...${accounts[0].slice(-4)}`,
        });
      } catch (error) {
        toast({
          title: "Connection Failed",
          description: "Failed to connect to Metamask wallet",
          variant: "destructive",
        });
      }
    } else {
      window.open('https://metamask.io/download/', '_blank');
    }
  };

  const handlePaymentSuccess = () => {
    setStep(3);
    setTimeout(() => {
      window.location.href = '/';
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-dark-base text-white flex flex-col justify-center px-6">
      <div className="max-w-md mx-auto w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => window.location.href = '/api/logout'}
              className="text-gray-400 hover:text-white"
              data-testid="button-back-to-login"
            >
              ← Sign Out
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => window.location.href = '/'}
              className="text-gray-400 hover:text-white"
              data-testid="button-skip-onboarding"
            >
              Skip Setup
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-neon-green mb-2">LPFTS WORLD WIDE™</h1>
          <p className="text-gray-300">Complete your account setup</p>
        </div>

        <Card className="bg-dark-elevated border-gray-700">
          <CardContent className="pt-6">
            {step === 1 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Step 1: Connect Your Wallet</h2>
                  {step > 1 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setStep(step - 1)}
                      className="text-gray-400 hover:text-white"
                      data-testid="button-back-step"
                    >
                      ← Back
                    </Button>
                  )}
                </div>
                <p className="text-gray-300 mb-6">
                  Connect your Metamask wallet to start earning Web3 rewards and NFT collectibles.
                </p>
                
                <Button 
                  onClick={connectWallet}
                  className={`w-full mb-4 ${
                    walletConnected 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  <Wallet className="mr-2" size={16} />
                  {walletConnected ? 'Wallet Connected' : 'Connect Metamask'}
                </Button>

                <Button 
                  onClick={() => setStep(2)}
                  className="w-full bg-neon-green text-dark-base font-semibold hover:bg-neon-green/90"
                >
                  Continue to Payment Setup
                </Button>
                
                <Button 
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                  className="w-full mt-2 border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Skip for now (Limited Access)
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold flex items-center">
                    <Crown className="mr-2 text-yellow-400" size={20} />
                    Step 2: VIP Membership Setup
                  </h2>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setStep(1)}
                    className="text-gray-400 hover:text-white"
                    data-testid="button-back-step"
                  >
                    ← Back
                  </Button>
                </div>
                <p className="text-gray-300 mb-6">
                  All users need VIP access to play LPFTS WORLD WIDE. Complete your payment setup to unlock:
                </p>
                
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex items-center text-green-400">
                    <Check size={16} className="mr-2" />
                    Unlimited street art discoveries
                  </div>
                  <div className="flex items-center text-green-400">
                    <Check size={16} className="mr-2" />
                    Token rewards and NFT collectibles
                  </div>
                  <div className="flex items-center text-green-400">
                    <Check size={16} className="mr-2" />
                    Access to all featured artists
                  </div>
                  <div className="flex items-center text-green-400">
                    <Check size={16} className="mr-2" />
                    Community events and competitions
                  </div>
                </div>

                {clientSecret ? (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm onSuccess={handlePaymentSuccess} />
                  </Elements>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center p-4 bg-yellow-900/20 border border-yellow-700 rounded">
                      <p className="text-yellow-300 text-sm">
                        Setting up payment processing...
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handlePaymentSuccess}
                      className="w-full bg-green-600 text-white font-semibold hover:bg-green-700"
                    >
                      Continue to Game (Demo Mode)
                    </Button>
                    
                    <Button 
                      onClick={() => window.location.href = '/subscribe'}
                      variant="outline"
                      className="w-full border-purple-500 text-purple-300 hover:bg-purple-900/20"
                    >
                      Setup Full Payment Later
                    </Button>
                  </div>
                )}
              </>
            )}

            {step === 3 && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-white" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">Welcome to LPFTS!</h2>
                  <p className="text-gray-300">Your account is fully set up. Redirecting to the game...</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}